import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import AboutData from './AboutData';
import { TermFullUWCoverageLengths } from '../../Products/Term/FullUWCoverageLengths';
import PurchasingProcessData from './PurchasingProcessData';
import FaqData from './FaqData';
import TermInsuranceData from './TermArticleData';
import PolicyCostData from '../../Products/TermInsurance/PolicyCostData';
import { 
  setBasicInfo,
  getBasicInfo,
  getSessionStorageItem,
  setSessionStorageItem,
  SESSION_STORAGE_ACCOUNT_KEY,
  SESSION_STORAGE_EMAIL_CAPTURE, 
  SESSION_STORAGE_SAVED_QUOTE_DATA
} from '../../../../../sessionStorage';
import TermAboutMeForm from '../../../../Quoters/CustomizeTerm/steps/AboutMeForm';
import ProductCarriers from './ProductCarriers';
import SaveQuotePartnerReferral from '../../../../Quoters/CustomizeTerm/SaveTermQuotePartnerReferral/SaveQuotePartnerReferral';
import { getPrimaryColor } from '../../../../Quoters/Common/Helpers/Utility';

export default function TermData(props) {
  const primaryBrandColor = getPrimaryColor();
  const minAge = 18;
  const maxAge = 70;
  const minCoverageAmount = 50000;
  const maxCoverageAmount = 10000000;
  const teaserQuoteParams = {
    faceAmount: 500000,
    termLength: 20,
    healthClass: 'PP',
  };

  const navigate = useNavigate();
  const location = useLocation();
  const accountObj = getSessionStorageItem(SESSION_STORAGE_ACCOUNT_KEY);
  const isPreQuoteEnabled = accountObj?.account?.enableTermPreQuote;
  const termNavigationState = props.isSimplifiedIssueTerm ? { state: { isFromSimplifiedIssueTerm: true } } : undefined;

  const accountProductTypeSettings = accountObj.accountProducts.find(product => props.isSimplifiedIssueTerm
    ? product.productLandingUrl === '/landing/simplified-issue-term'
    : product.productLandingUrl === location.pathname);
  const accountLandingOverride = accountObj.accountOverrides && accountProductTypeSettings ?
    accountObj.accountOverrides.find(override => override.accountProductTypeId === accountProductTypeSettings.productTypeId) : null;

  const emailCapture = getSessionStorageItem(SESSION_STORAGE_EMAIL_CAPTURE);
  const savedQuote = getSessionStorageItem(SESSION_STORAGE_SAVED_QUOTE_DATA);
  const basicInfo = getBasicInfo();

  const [isQuoteSaved, setQuoteSaved] = useState(savedQuote.quoteSaved);
  const [errorMessage, setErrorMessage] = useState({ message: '', status: '' });

  const [saveQuoteData, setSaveQuoteData] = useState(null);
  const [saveQuoteContact, setSaveQuoteContact] = useState(savedQuote);

  const [emailCaptureActive, setEmailCaptureActive] = useState(false);
  const [triggerNext, setTriggerNext] = useState(false);
  const [enableInitialEmailCapture, setEnableInitialEmailCapture] = useState(accountObj.account.enableInitialEmailCapture);
  const [enableTimeoutEmailCapture, setEnableTimeoutEmailCapture] = useState(accountObj.account.enableTimeoutEmailCapture);
  const [initialEmailCaptureDisplayed, setInitialEmailCaptureDisplayed] = useState(emailCapture.initialEmailCaptureDisplayed || false);
  const [timeoutEmailCaptureDisplayed, setTimeoutEmailCaptureDisplayed] = useState(emailCapture.timeoutEmailCaptureDisplayed || false);
  const [enablePartnerReferralEmailCapture, setEnablePartnerReferralEmailCapture] = useState(emailCapture.enablePartnerReferralEmailCapture || false);
  const [isPartnerReferralEmailCaptureActive, setIsPartnerReferralEmailCaptureActive] = useState(emailCapture.isPartnerReferralEmailCaptureActive || !isQuoteSaved && emailCapture.enablePartnerReferralEmailCapture ? true : false);
  const [modalSaveQuoteIsOpen, setModalSaveQuoteIsOpen] = useState(false);

  const setCurrentSavedQuoteData = () => {
    const newData = getSessionStorageItem(SESSION_STORAGE_SAVED_QUOTE_DATA);
    setSaveQuoteContact(newData);
  };

  const handleContactChange = (contact) => {
    setSaveQuoteContact(contact);
  };

  const setTimeoutEmailDisplayed = () => {
    setTimeoutEmailCaptureDisplayed(true);
  };

  const setInitialEmailDisplayed = () => {
    setInitialEmailCaptureDisplayed(true);
  };

  const setEmailPrefill = () => {
    setBasicInfo(basicInfo);
  };

  const onSubmit = () => {
    setEmailCaptureActive(false);
    if (props.aboutMeFormRef.current) {
      props.aboutMeFormRef.current.onSubmit();
    }
  };

  useEffect(() => {
    if (saveQuoteContact.emailAddress !== "") {
      setSessionStorageItem(SESSION_STORAGE_SAVED_QUOTE_DATA, {
        "quoteSaved": isQuoteSaved,
        "firstName": saveQuoteContact.firstName,
        "lastName": saveQuoteContact.lastName,
        "emailAddress": saveQuoteContact.emailAddress,
      });
    }
    setCurrentSavedQuoteData();
  }, [isQuoteSaved]);

  useEffect(() => {
      if (triggerNext && !emailCaptureActive) {
        if (props.isTermInsurance) {
          navigate(isPreQuoteEnabled ? '/term-insurance/prequote' : '/term-insurance/quote-results', termNavigationState);
        } else {
          navigate(isPreQuoteEnabled ? '/quote/term-life-insurance/multi-carrier-term/pre-quote' : '/quote/term-life-insurance/multi-carrier-term/quote-results');
        }
      }
    }, [triggerNext, emailCaptureActive, isPreQuoteEnabled, props.isSimplifiedIssueTerm]);

  const mctForm = (<TermAboutMeForm
    ageSpecs={{ min: minAge, max: maxAge }}
    onNext={() => {
      setTriggerNext(true);
    }}
    brandTheme={primaryBrandColor}
    showHeader={false}
    submitButtonVariant={'landing'}
    isTermInsurance={props.isTermInsurance}
    teaserQuoteParams={teaserQuoteParams}
    showTestimonialPanel={true}
    term30EnableTeaserRate={accountObj.term30EnableTeaserRate}
    ref={props.aboutMeFormRef}
    setModalSaveQuoteIsOpen={setModalSaveQuoteIsOpen}
    setSaveQuoteData={setSaveQuoteData}
    saveQuoteContact={saveQuoteContact}
    emailCapture={emailCapture}
    setEmailCaptureActive={setEmailCaptureActive}
    isSimplifiedIssueTerm={props.isSimplifiedIssueTerm}
  />);

  const defaultValueProposition = [{
    title: "Term Life Insurance",
    subtitle: "Protect Your Family: Plan Their Future",
    subtitleExtended: "Term life insurance is the most affordable way to secure coverage.",
  }];

  return {
    name: 'Term Life Insurance',
    subtitle: 'Click. Quote. Coverage.',
    footerMessage: "Get term insurance from start to finish completely online",
    footerButtonName: 'SEE YOUR QUOTES',
    heroText: accountLandingOverride ? JSON.parse(accountLandingOverride.valueProposition) : defaultValueProposition,
    basicInfoForm: () => {
      return mctForm;
    },
    about: AboutData(minAge, maxAge, minCoverageAmount, maxCoverageAmount, TermFullUWCoverageLengths()),
    purchasingProcess: PurchasingProcessData(),
    policyCost: PolicyCostData(),
    faq: FaqData(),
    termArticle: TermInsuranceData(),
    productCarriers: ProductCarriers(),
    isAddDefaultFaq: false,
    saveMyQuotePartnerReferral: <SaveQuotePartnerReferral 
      type="landing-screen"
      title="Almost there!"
      setQuoteSaved={setQuoteSaved}
      isQuoteSaved={isQuoteSaved}
      setEmailPrefill={(e) => setEmailPrefill(e)}
      errorMessage={errorMessage}
      setErrorMessage={setErrorMessage}
      setEmailModal={{
        enableInitialEmailCapture: enableInitialEmailCapture,
        enableTimeoutEmailCapture: enableTimeoutEmailCapture,
        initialEmailCaptureDisplayed: initialEmailCaptureDisplayed,
        timeoutEmailCaptureDisplayed: timeoutEmailCaptureDisplayed,
        isPartnerReferralEmailCaptureActive: isPartnerReferralEmailCaptureActive,
        enablePartnerReferralEmailCapture: enablePartnerReferralEmailCapture,
      }}
      modalSaveQuoteIsOpen={modalSaveQuoteIsOpen}
      setModalSaveQuoteIsOpen={setModalSaveQuoteIsOpen}
      setTimeoutEmailDisplayed={() => setTimeoutEmailDisplayed()}
      setInitialEmailDisplayed={() => setInitialEmailDisplayed()}
      saveQuoteData={saveQuoteData}
      handleContactChange={handleContactChange}
      onSubmit={onSubmit}
    />,
  };
}
